import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import DataToolbar from "../../components/common/DataToolbar";
import BackdropLoader from "../../components/common/BackdropLoader";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../store";
import {
  setDateRange,
  setPage,
  setRowsPerPage,
  setSearchTerm,
  setSort,
} from "../../store/slices/tableSlice";
import DataTable from "../../components/common/DataTable";
import {
  useGetMaterialMasterQuery,
  useSyncMaterialFromSapMutation,
} from "../../api/services/materialApi";
import type { SpareItem } from "./spareSlice";
import { useLazyGetAllPlantsDetailsQuery } from "../../api/services/plantApi";

const Spares: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    searchTerm,
    dateRange,
    rowsPerPage,
    currentPage,
    sortField,
    sortOrder,
  } = useSelector((state: any) => state.table);
  // Debounce Search Input
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [plantFilter, setPlantFilter] = useState("");
  // API Call
  const { data, isLoading, error, isFetching } = useGetMaterialMasterQuery({
    page: currentPage,
    limit: rowsPerPage,
    q: debouncedSearchTerm?.trim() || undefined,
    plant: plantFilter || undefined,
  });
  useEffect(() => {
  dispatch(setPage(1));
}, [plantFilter, dispatch]);


  const [getPlants, { data: plantsData }] = useLazyGetAllPlantsDetailsQuery();

  const [syncMaterial, { isLoading: isSyncing }] =
    useSyncMaterialFromSapMutation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      dispatch(setPage(1));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    getPlants({ pageNum: 1, search: "" });
  }, [getPlants]);

  // Converted response items
  const sparesData: SpareItem[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((item: any) => ({
      id: String(item.MATERIAL_ID ?? ""),
      materialName: String(item.MATERIAL_NAME ?? ""),
      abcIndicator: String(item.MATERIAL_CATEGORY ?? ""),
      plant: String(item.PLANT ?? ""),
      materialType: String(item.MATERIAL_TYPE ?? ""),
      materialGroup: String(item.MATERIAL_GROUP ?? ""),
      companyCode: String(item.COMPANY_CODE ?? ""),
      materialBaseUnit: String(item.MATERIAL_BASE_UNIT ?? ""),
      active: item.ACTIVE ?? false,
    }));
  }, [data]);

  const plantOptions = useMemo(() => {
    if (!plantsData?.plants) return [];

    return plantsData.plants.map((p: any) => p.name); // A110, A112
  }, [plantsData]);

  const plantOptionsWithAll = useMemo(() => {
    return ["", ...plantOptions]; // "" = All Plant
  }, [plantOptions]);

  // Sorting state

  // Apply Sorting
  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return sparesData;

    return [...sparesData].sort((a: any, b: any) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [sparesData, sortField, sortOrder]);

  const totalPages = data?.pagination?.totalPages ?? 1;

  // DataTable Columns
  const columns = [
    { key: "id", label: "Material ID", sortable: true },
    { key: "materialName", label: "Material Name", sortable: true },
    {
      key: "plant",
      label: "Plant Code",
      sortable: true,
      filter: {
        value: plantFilter,
        options: plantOptionsWithAll,
        onChange: setPlantFilter,
      },
    },
    { key: "abcIndicator", label: "ABC Indicator", sortable: true },
  ];

  const handleSync = async () => {
    try {
      await syncMaterial().unwrap();
    } catch (err) {
      console.error("Sync failed:", err);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 3 },
      }}
    >
      <BackdropLoader openStates={isLoading || isSyncing || isFetching} />

      <Box sx={{ position: "sticky", top: 0, zIndex: 10, pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Spares
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage spare part requests approved by managers. Review
              details, verify availability, and process spares for issue or
              procurement.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={isSyncing}
            sx={{
              backgroundColor: "#002060",
              color: "#fff",
              borderRadius: 4,
              textTransform: "none",
              fontSize: 12,
            }}
          >
            {isSyncing && (
              <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />
            )}
            Refetch Spare from SAP
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load spares data: {JSON.stringify(error)}
        </Alert>
      )}

      <DataToolbar
        searchTerm={searchTerm}
        setSearchTerm={(val) => dispatch(setSearchTerm(val))}
        dateRange={dateRange}
        setDateRange={(val) => dispatch(setDateRange(val))}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={(val) => dispatch(setRowsPerPage(val))}
        currentPage={currentPage}
        setCurrentPage={(val) => dispatch(setPage(val))}
        totalPages={totalPages}
      />

      <DataTable
        key={`${currentPage}-${plantFilter}-${debouncedSearchTerm}`}
        columns={columns}
        data={sortedData}
        getRowId={(row) => row.id}
        showCheckbox
        emptyMessage="No data available"
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(field) => dispatch(setSort({field}))}
      />
    </Box>
  );
};

export default Spares;
